import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Paper, Typography } from "@material-ui/core";
import { useChannelPlugins } from "../../components/ChannelIcon";
import PluginHtmlInline from "../../components/PluginBridge/PluginHtmlInline";
import PluginJsxInline from "../../components/PluginBridge/PluginJsxInline";
import api from "../../services/api";

/**
 * Página INSERIDA de um plugin: `/plugin/:pluginId/:actionId`.
 *
 * É o que uma action com `openType: "page"` abre — no lugar do conteúdo, com
 * navbar e menu lateral do app em volta, dentro da sessão (nada de iframe).
 * O conteúdo vem de duas formas, na mesma ordem de preferência do resto do
 * sistema:
 *  - `resultType: "jsx"` + `resultJsx`: React do app; se houver `endpoint`,
 *    o POST acontece antes e a resposta chega em `props.dados`;
 *  - senão `endpoint`: HTML devolvido pelo plugin, injetado na sessão
 *    (scripts inline rodam — PluginHtmlInline).
 *
 * A action não precisa estar no menu lateral: qualquer botão do plugin pode
 * navegar para cá (`runtime.actions.navigate("/plugin/<id>/<action>")`).
 */
export default function PluginPage() {
  const { pluginId, actionId } = useParams();
  const plugins = useChannelPlugins();
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(false);

  const alvo = useMemo(() => {
    for (const plugin of plugins) {
      for (const action of plugin.provides?.actions || []) {
        if (plugin.id === pluginId && action.id === actionId) return { plugin, action };
      }
    }
    return null;
  }, [plugins, pluginId, actionId]);

  const routePath = alvo?.plugin?.routePath || "";
  const endpoint = useMemo(() => {
    const ep = alvo?.action?.endpoint;
    if (!ep) return "";
    return ep.startsWith("/") ? ep : `/${ep}`;
  }, [alvo]);
  const ehJsx = alvo?.action?.resultType === "jsx" && !!alvo?.action?.resultJsx;

  useEffect(() => {
    let vivo = true;
    if (!ehJsx || !routePath || !endpoint) { setDados(null); return undefined; }
    setCarregando(true);
    api.post(`${routePath}${endpoint}`, { pluginId, actionId })
      .then((res) => { if (vivo) setDados(res?.data ?? null); })
      .catch((e) => { if (vivo) setDados({ erro: String(e?.message || e) }); })
      .finally(() => { if (vivo) setCarregando(false); });
    return () => { vivo = false; };
  }, [ehJsx, routePath, endpoint, pluginId, actionId]);

  const pluginApi = useMemo(() => ({
    get: (caminho, config) => api.get(`${routePath}${caminho}`, config),
    post: (caminho, corpo, config) => api.post(`${routePath}${caminho}`, corpo, config),
  }), [routePath]);

  const jsxProps = useMemo(
    () => ({ dados, carregando, routePath, api: pluginApi, pluginId, actionId }),
    [dados, carregando, routePath, pluginApi, pluginId, actionId]
  );

  if (!alvo) {
    return (
      <Paper style={{ margin: 16, padding: 24 }}>
        <Typography variant="body1">Página de plugin não encontrada ({pluginId}/{actionId}).</Typography>
      </Paper>
    );
  }

  return (
    <div style={{ padding: 16 }} data-plugin-page={`${pluginId}/${actionId}`}>
      {ehJsx ? (
        <PluginJsxInline block jsx={alvo.action.resultJsx} props={jsxProps} />
      ) : (
        <PluginHtmlInline block routePath={routePath} endpoint={endpoint || `/${actionId}`} context={{ pluginId, actionId }} />
      )}
    </div>
  );
}
